import React, { useState, useEffect, useRef } from "react";
import { Controller, useWatch, useFormContext } from "react-hook-form";
import { buildingAddressService } from "../../../../services/buildingAddressService";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Dialog, DialogContent, DialogTitle } from "../../../../shadcn/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";
import { HelpCircle, Plus, Trash2, Layers, Edit3 } from "lucide-react";
import LocationPickerModal from "./LocationPickerModal";
import InlineBuildingAddressForm from "./InlineBuildingAddressForm";
import ManageFloorsDialog from "./ManageFloorsDialog";
import SearchableBuildingAddress from "./SearchableBuildingAddress";
import HSimpleFloorPlan from "../../../../hcomponents/HSimpleFloorPlan";
import { showApiMessage } from "../../../../utils/messageHandler";
import { getCurrentUserOrganizationId } from "../../host-clients/utils/hostClientsUtils";
import logger from "../../../../utils/logger";


const FieldTip = ({ children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle className="w-4 h-4 ml-1 text-gray-400 cursor-help" />
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs">
      {children}
    </TooltipContent>
  </Tooltip>
);

const SpaceInstancesCard = ({
  control,
  errors,
  instanceFields,
  addInstance,
  removeInstance,
  isReadOnly = false,
  fetchingSpace = false,
  buildingFloors = {},
  loadingFloors = {},
  setBuildingFloors = () => {},
  fetchFloorsForBuilding = () => {},
}) => {
  const { setValue } = useFormContext();
  const [floorPlanEditorOpen, setFloorPlanEditorOpen] = useState(false);
  const [currentEditingFloor, setCurrentEditingFloor] = useState(null);
  const [currentBuildingId, setCurrentBuildingId] = useState(null);
  const floorPlanRef = useRef(null);
  const watchedInstances = useWatch({ control, name: "instances" }) || [];
  const watchedGenerationMode = useWatch({ control, name: "instanceGenerationMode" }) || "INDIVIDUAL";
  const watchedQuantity = useWatch({ control, name: "instanceQuantity" }) || 1;

  const handleAddressCreated = (newAddress, index) => {
    if (newAddress && newAddress.id) {
      // Auto-select the newly created address for this instance
      setValue(`instances.${index}.buildingAddressId`, newAddress.id, {
        shouldValidate: true,
        shouldDirty: true,
      });

      // Update coordinates from the new address
      if (newAddress.latitude != null && newAddress.longitude != null) {
        setValue(`instances.${index}.coordinates`, {
          latitude: newAddress.latitude,
          longitude: newAddress.longitude,
        });
      }

      // Fetch floors for the new building to enable floor selection
      fetchFloorsForBuilding(newAddress.id);
    }
  };

  // Fetch logic and sync moved to parent HostAddSpace

  // Open floor plan editor for a specific floor
  const openFloorPlanEditor = (buildingId, floor) => {
    setCurrentBuildingId(buildingId);
    setCurrentEditingFloor(floor);
    setFloorPlanEditorOpen(true);
  };

  // Save floor plan changes
  const handleFloorPlanSave = async () => {
    if (!floorPlanRef.current || !currentEditingFloor || !currentBuildingId) return;

    try {
      const saveData = floorPlanRef.current.getSaveData();
      const newLayout = Array.isArray(saveData) ? saveData : Array.isArray(saveData?.layout) ? saveData.layout : [];

      // Update the floor plan in the backend
      const updatedFloor = {
        ...currentEditingFloor,
        floorPlan: { layout: newLayout },
      };

      const payload = {
        floors: buildingFloors[currentBuildingId].map(f => (f.id === currentEditingFloor.id ? updatedFloor : f)),
      };

      const response = await buildingAddressService.patchBuildingAddress(currentBuildingId, payload);

      if (response.status >= 200 && response.status < 300) {
        // Update local cache
        setBuildingFloors(prev => ({
          ...prev,
          [currentBuildingId]: prev[currentBuildingId].map(f => (f.id === currentEditingFloor.id ? updatedFloor : f)),
        }));

        showApiMessage(response, "update floor plan");
        setFloorPlanEditorOpen(false);
      } else {
        showApiMessage(response, "update floor plan");
      }
    } catch (error) {
      logger.error("Error saving floor plan:", error);
      showApiMessage(error, "update floor plan");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">
              {watchedGenerationMode === "BULK"
                ? "Bulk Unit Setup"
                : instanceFields.length > 1
                  ? "Space Units"
                  : "Space Location"}
            </CardTitle>
            <CardDescription className="text-sm text-gray-500 mt-1">
              {watchedGenerationMode === "BULK"
                ? "Specify details for one unit and we'll replicate them to all"
                : instanceFields.length > 1
                  ? "Create multiple units of this space type with their own locations"
                  : "Where the space is physically located"}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Controller
              name="instanceGenerationMode"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={isReadOnly}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                    <SelectItem value="BULK">Bulk</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {watchedGenerationMode === "INDIVIDUAL" && (
              <Button variant="outline" size="sm" onClick={addInstance} disabled={isReadOnly} type="button">
                <Plus className="w-4 h-4 mr-2" />
                Add Unit
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <TooltipProvider>
          {watchedGenerationMode === "BULK" && (
            <div className="mb-6">
              <Label htmlFor="instance-quantity" className="flex items-center mb-1">
                Number of Units{" "}
                <FieldTip>How many identical units to create. Each can be managed individually later.</FieldTip>
              </Label>
              <Controller
                name="instanceQuantity"
                control={control}
                render={({ field }) => (
                  <Input
                    id="instance-quantity"
                    type="number"
                    min={1}
                    max={50}
                    placeholder="e.g. 10"
                    value={field.value}
                    onChange={e => {
                      const val = e.target.value;
                      if (val === "") {
                        field.onChange("");
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num)) field.onChange(num);
                      }
                    }}
                    disabled={isReadOnly}
                    className={errors.instanceQuantity ? "border-red-500" : ""}
                  />
                )}
              />
              {errors.instanceQuantity && (
                <p className="text-sm text-red-500 mt-1">{errors.instanceQuantity.message}</p>
              )}
            </div>
          )}
          {instanceFields.map((instance, index) => {
            // In BULK mode, only show the first instance (the template) in the UI
            if (watchedGenerationMode === "BULK" && index > 0) return null;

            const buildingId = watchedInstances?.[index]?.buildingAddressId;
            const isLoadingFloorsForThisBuilding = buildingId
              ? !buildingFloors[buildingId] || !!loadingFloors[buildingId]
              : false;

            return (
              <div key={instance.id} className={`space-y-4 ${index > 0 ? "pt-6 border-t border-gray-100" : ""}`}>
                {/* Instance Header for Individual Mode */}
                {watchedGenerationMode === "INDIVIDUAL" && instanceFields.length > 1 && (
                  <div className="flex justify-between items-center bg-gray-50/50 p-2 rounded-md">
                    <span className="text-sm font-semibold text-gray-700">Unit {index + 1}</span>
                    {!isReadOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeInstance(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        type="button"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                )}

                {/* Instance Name */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor={`instance-name-${index}`} className="flex items-center mb-1">
                      Space Name{" "}
                      <FieldTip>
                        A descriptive name for this unit, e.g. "Conference Room A". Helps customers identify the exact
                        space.
                      </FieldTip>
                    </Label>
                    <Controller
                      name={`instances.${index}.name`}
                      control={control}
                      render={({ field }) => (
                        <Input
                          id={`instance-name-${index}`}
                          placeholder={
                            watchedGenerationMode === "BULK"
                              ? "e.g., Office Space"
                              : "e.g., 5th Floor - Conference Room A"
                          }
                          disabled={isReadOnly}
                          {...field}
                        />
                      )}
                    />
                    {errors.instances?.[index]?.name && (
                      <p className="text-sm text-red-500 mt-1">{errors.instances[index].name.message}</p>
                    )}
                  </div>
                </div>

                {/* Building Address */}
                <div>
                  <Label htmlFor={`instance-address-${index}`} className="flex items-center mb-1">
                    Building Address{" "}
                    <FieldTip>
                      The physical location of this space. Select an existing address or add a new one.
                    </FieldTip>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Controller
                      name={`instances.${index}.buildingAddressId`}
                      control={control}
                      render={({ field }) => (
                        <div className="flex-1 min-w-0">
                          <SearchableBuildingAddress
                            value={field.value}
                            onChange={buildingId => {
                              field.onChange(buildingId);
                              // Fetch floors when building changes
                              if (buildingId) {
                                fetchFloorsForBuilding(buildingId);
                              }
                            }}
                            onBuildingSelect={building => {
                              // Update coordinates if building has them
                              if (building.latitude && building.longitude) {
                                setValue(`instances.${index}.coordinates`, {
                                  latitude: building.latitude,
                                  longitude: building.longitude,
                                });
                              }
                            }}
                            disabled={isReadOnly}
                            placeholder="Search building addresses..."
                            className="w-full"
                          />
                        </div>
                      )}
                    />
                    <div className="flex-shrink-0">
                      <InlineBuildingAddressForm
                        onAddressCreated={newAddress => handleAddressCreated(newAddress, index)}
                        triggerButton={
                          <Button variant="outline" size="icon" disabled={isReadOnly} type="button">
                            <Plus className="w-4 h-4" />
                          </Button>
                        }
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <ManageFloorsDialog
                        buildingAddressId={watchedInstances?.[index]?.buildingAddressId || null}
                        isReadOnly={isReadOnly}
                        triggerButton={
                          <Button variant="outline" size="sm" type="button" disabled={isReadOnly}>
                            <Layers className="w-4 h-4 mr-2" />
                            Manage Floors
                          </Button>
                        }
                        onUpdated={updatedBuilding => {
                          // Refresh floors cache when floors are updated
                          if (updatedBuilding && updatedBuilding.id) {
                            setBuildingFloors(prev => ({
                              ...prev,
                              [updatedBuilding.id]: updatedBuilding.floors || [],
                            }));
                          }
                        }}
                      />
                    </div>
                  </div>
                  {errors.instances?.[index]?.buildingAddressId && (
                    <p className="text-sm text-red-500 mt-1">{errors.instances[index].buildingAddressId.message}</p>
                  )}
                </div>

                {/* Floor Selection with Edit Floor Plan */}
                <div>
                  <Label className="flex items-center mb-1">
                    Floor Selection{" "}
                    <FieldTip>
                      Choose which floor this space is on within the selected building. You can also edit the floor
                      plan.
                    </FieldTip>
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">Select a floor from the building and edit its floor plan</p>
                  <div className="flex items-center gap-2">
                    <Controller
                      name={`instances.${index}.floorLevel`}
                      control={control}
                      render={({ field: floorField }) => {
                        // Use watchedInstances to get buildingAddressId - more reliable than nested Controller
                        const currentBuildingId = watchedInstances?.[index]?.buildingAddressId;
                        const floors = currentBuildingId ? buildingFloors[currentBuildingId] || [] : [];
                        const currentOrgId = getCurrentUserOrganizationId();
                        // Check if floors are loading OR haven't been fetched yet (buildingFloors entry is undefined)
                        const isLoadingFloorsForThisBuilding = currentBuildingId
                          ? !buildingFloors[currentBuildingId] || !!loadingFloors[currentBuildingId]
                          : false;

                        const selectedFloor = floors.find(f => f.floorNumber?.toString() === floorField.value);
                        // Check if we have a value but no matching floor in the floors array
                        const hasMatchingFloor = floors.some(f => f.floorNumber?.toString() === floorField.value);
                        // Show floor value while loading OR when we have a value but floors aren't loaded yet
                        // Note: floorField.value could be "0" which is truthy, so we need explicit check
                        const hasFloorValue =
                          floorField.value !== "" && floorField.value !== null && floorField.value !== undefined;
                        const showLoadingWithValue =
                          currentBuildingId &&
                          hasFloorValue &&
                          (isLoadingFloorsForThisBuilding || (!hasMatchingFloor && floors.length === 0));

                        return (
                          <>
                            {showLoadingWithValue ? (
                              // Show current floor value while floors are loading
                              <div className="flex-1 h-10 px-3 flex items-center border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600">
                                <span>Floor {floorField.value}</span>
                                <span className="ml-2 text-xs text-gray-400">(loading...)</span>
                              </div>
                            ) : (
                              <Select
                                value={floorField.value || ""}
                                onValueChange={value => {
                                  floorField.onChange(value);
                                  // Also update floorId when floor is selected
                                  const selectedFloor = floors.find(f => f.floorNumber?.toString() === value);
                                  if (selectedFloor?.id) {
                                    setValue(`instances.${index}.floorId`, selectedFloor.id, {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    });
                                  }
                                }}
                                disabled={!currentBuildingId || isReadOnly}
                              >
                                <SelectTrigger className="flex-1">
                                  <SelectValue
                                    placeholder={!currentBuildingId ? "Select building first" : "Select floor"}
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {floors.length > 0 ? (
                                    floors.map(floor => {
                                      const isFloorLocked = !currentOrgId || floor.organizationId !== currentOrgId;
                                      return (
                                        <SelectItem
                                          key={floor.id}
                                          value={floor.floorNumber?.toString() || ""}
                                          disabled={isFloorLocked}
                                        >
                                          {floor.label || `Floor ${floor.floorNumber}`}
                                          {isFloorLocked ? " (locked)" : ""}
                                        </SelectItem>
                                      );
                                    })
                                  ) : (
                                    <SelectItem value="no-floors" disabled>
                                      {!currentBuildingId
                                        ? "Select a building first"
                                        : "No floors found. Use Manage Floors to add some."}
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            )}

                            {selectedFloor && (
                              <Button
                                variant="outline"
                                size="sm"
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => openFloorPlanEditor(currentBuildingId, selectedFloor)}
                              >
                                <Edit3 className="w-4 h-4 mr-2" />
                                Edit Floor Plan
                              </Button>
                            )}
                          </>
                        );
                      }}
                    />
                  </div>
                  {(errors.instances?.[index]?.floorLevel || errors.instances?.[index]?.floorId) &&
                    !isLoadingFloorsForThisBuilding &&
                    !fetchingSpace && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.instances[index].floorId?.message || errors.instances[index].floorLevel?.message}
                      </p>
                    )}
                </div>
              </div>
            );
          })}
        </TooltipProvider>
      </CardContent>

      {/* Floor Plan Editor Dialog */}
      <Dialog open={floorPlanEditorOpen} onOpenChange={setFloorPlanEditorOpen}>
        <DialogContent className="sm:max-w-[960px] w-full max-h-[90vh] overflow-hidden p-0">
          <div className="flex flex-col h-[80vh]">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <DialogTitle>
                Edit Floor Plan - {currentEditingFloor?.label || `Floor ${currentEditingFloor?.floorNumber}`}
              </DialogTitle>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setFloorPlanEditorOpen(false)} type="button">
                  Cancel
                </Button>
                <Button variant="spaciadefault" onClick={handleFloorPlanSave} type="button">
                  Save Floor Plan
                </Button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              {currentEditingFloor && (
                <HSimpleFloorPlan
                  ref={floorPlanRef}
                  initialLayout={currentEditingFloor.floorPlan?.layout || []}
                  onSave={() => {}}
                  className="w-full h-full"
                  useIcons={true}
                  useColors={false}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SpaceInstancesCard;
